import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ErrorState from "@/components/base/ErrorState";
import SectionHeading from "@/components/base/SectionHeading";
import PlayerModal from "@/pages/detail/components/PlayerModal";
import RelatedList from "@/pages/detail/components/RelatedList";
import { useAsyncData } from "@/hooks/useAsyncData";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getCategoryById, getRelatedVideos, getVideoById } from "@/utils/video";
import type { Video } from "@/types";

export default function DetailPage() {
  const { videoId = "" } = useParams();
  const [playerOpen, setPlayerOpen] = useState(false);
  const [toast, setToast] = useState(false);

  const { data, loading, error, retry } = useAsyncData<Video | null>(
    () => getVideoById(videoId) ?? null,
    [videoId],
  );

  const video = data;
  const category = video ? getCategoryById(video.categoryId) : undefined;

  usePageMeta(
    video ? `${video.title} - 在线观看高清视频` : "视频详情 - 精品视频",
    video ? `${video.title}：${video.description.slice(0, 70)}` : "精品视频 - 高清免费视频在线观看。",
  );

  useEffect(() => {
    setPlayerOpen(false);
  }, [videoId]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      /* clipboard may be unavailable; still show feedback */
    }
    setToast(true);
    setTimeout(() => setToast(false), 2000);
  };

  if (loading) {
    return (
      <div className="w-full px-4 md:px-8 pt-24 md:pt-28 pb-16">
        <div className="max-w-5xl mx-auto animate-pulse">
          <div className="w-full aspect-video rounded-lg bg-background-200" />
          <div className="mt-6 h-7 w-2/3 rounded bg-background-200" />
          <div className="mt-4 h-4 w-1/3 rounded bg-background-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-4 md:px-8 pt-24 md:pt-28 pb-16">
        <ErrorState onRetry={retry} />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 pt-24 md:pt-28 pb-16">
        <EmptyState
          icon="ri-file-unknow-line"
          title="视频不存在或已下架"
          description="你访问的视频可能已被移除或链接有误，去看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const related = getRelatedVideos(video, 6);

  const attrRows = [
    { label: "类型", value: video.type },
    { label: "地区", value: video.region },
    { label: "年份", value: String(video.year) },
    { label: "时长", value: video.duration },
    { label: "评分", value: `${video.rating} 分` },
    { label: "播放量", value: video.views },
    { label: "状态", value: video.episode },
  ];

  return (
    <div className="w-full px-4 md:px-8 pt-24 md:pt-28 pb-16 md:pb-20">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          ...(category ? [{ label: category.name, to: `/category/${category.id}` }] : []),
          { label: video.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 主内容 */}
        <div className="lg:col-span-2">
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-primary-950 border border-background-200">
            <img
              src={video.cover}
              alt={`${video.title} 封面大图`}
              title={`${video.title} - 高清视频在线观看`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/20 to-primary-950/30" />
            <span className="absolute top-4 left-4 px-3 py-1 rounded-md bg-secondary-500 text-background-50 text-sm font-medium">
              {video.type}
            </span>
            <span className="absolute top-4 right-4 px-2.5 py-1 rounded-md bg-primary-950/70 text-secondary-300 text-sm font-semibold">
              {video.rating} 分
            </span>
            <button
              type="button"
              onClick={() => setPlayerOpen(true)}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 cursor-pointer group"
              aria-label="播放视频演示"
            >
              <span className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-secondary-500 text-background-50 group-hover:bg-secondary-600 transition-colors">
                <i className="ri-play-fill text-4xl md:text-5xl" />
              </span>
              <span className="text-background-100 text-sm font-medium">点击播放（演示）</span>
            </button>
          </div>

          <p className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-information-line text-accent-500" />
            播放区为前端演示占位，未接入真实播放接口，真实播放需后续接口支持。
          </p>

          <h1 className="mt-5 text-2xl md:text-3xl font-black text-foreground-950 leading-snug">
            {video.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-foreground-600">
            <span className="text-accent-600 font-medium">{video.rating} 分</span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-300" />
            <span>{video.year}</span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-300" />
            <span>{video.region}</span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-300" />
            <span>{video.duration}</span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-300" />
            <span>{video.views} 次播放</span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-300" />
            <span>{video.episode}</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {video.tags.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                {t}
              </span>
            ))}
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-bold text-foreground-950 mb-2">内容简介</h2>
            <p className="text-sm leading-relaxed text-foreground-700">{video.description}</p>
          </div>

          <div className="mt-10">
            <SectionHeading icon="ri-thumb-up-line" title="相关推荐" subtitle="根据所属分类为你推荐相似内容" />
            <RelatedList videos={related} />
          </div>
        </div>

        {/* 侧栏 */}
        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-bold text-foreground-950 mb-4">视频信息</h2>
            <dl className="space-y-3">
              {attrRows.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-4 text-sm">
                  <dt className="text-foreground-500 shrink-0">{row.label}</dt>
                  <dd className="text-foreground-900 font-medium text-right">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => setPlayerOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-play-fill text-lg" />
              开始观看（演示）
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-background-200 bg-background-50 text-foreground-700 text-sm font-semibold hover:border-secondary-400 hover:text-secondary-600 whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-share-forward-line" />
              复制分享链接
            </button>
            {category && (
              <Link
                to={`/category/${category.id}`}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-primary-100 hover:text-primary-700 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className={category.icon} />
                进入「{category.name}」分类
              </Link>
            )}
          </div>
        </aside>
      </div>

      <PlayerModal video={video} open={playerOpen} onClose={() => setPlayerOpen(false)} />

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-medium animate-fadeUp">
          <span className="inline-flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-accent-400" />
            链接已复制到剪贴板
          </span>
        </div>
      )}
    </div>
  );
}